import React, { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
} from "@mui/material";
import { Edit, AddShoppingCart, Delete } from "@mui/icons-material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import apiVentas from "../apiVentas";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";

// Categorías
const tiposVenta = ["Ganado-Carne", "Ganado-En Pie", "Granos", "Otro"];

const Ventas = () => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [dbUser, setDbUser] = useState("");
  const [ventas, setVentas] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedVenta, setSelectedVenta] = useState(null);

  // Estado inicial para una nueva venta
  const emptyVenta = {
    fecha: new Date().toISOString().split("T")[0],
    tipo: "Ganado-Carne",
    productos: [],
    plazo: 0,
    comprador: "",
    total_neto: 0,
    observaciones: "",
  };

  const [ventaData, setVentaData] = useState(emptyVenta);

  // Autenticación Firebase
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user || null);
      setDbUser(user ? user.displayName || user.uid : "");
    });
    return () => unsubscribe();
  }, []);

  // Cargar ventas desde Firestore
  useEffect(() => {
    if (!dbUser) return;
    apiVentas.getVentas().then(setVentas);
  }, [dbUser]);

  // Abrir modal de edición/creación
  const openVentaModal = (venta = null) => {
    setVentaData(venta ? { ...venta } : emptyVenta);
    setSelectedVenta(venta);
    setOpenModal(true);
  };

  // Manejar cambios en los inputs
  const handleChange = (e) => {
    setVentaData({ ...ventaData, [e.target.name]: e.target.value });
  };

  // Agregar un producto nuevo a la venta
  const handleAddProduct = () => {
    setVentaData({
      ...ventaData,
      productos: [...ventaData.productos, { kg: 0, precio: 0, categoria: "" }],
    });
  };

  // Editar un producto en la lista
  const handleProductChange = (index, field, value) => {
    const updatedProductos = ventaData.productos.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );
    setVentaData({ ...ventaData, productos: updatedProductos });
  };

  // Eliminar un producto de la lista
  const handleDeleteProduct = (index) => {
    setVentaData({
      ...ventaData,
      productos: ventaData.productos.filter((_, i) => i !== index),
    });
  };

  // Guardar cambios en Firebase
  const handleSave = async () => {
    if (selectedVenta) {
      await apiVentas.updateVenta(ventaData);
      setVentas(ventas.map((v) => (v.id === ventaData.id ? ventaData : v)));
    } else {
      await apiVentas.createVenta(ventaData);
      setVentas([...ventas, ventaData]);
    }
    setOpenModal(false);
  };

  // Columnas de la tabla
  const columns = [
    { Header: "Fecha", accessor: "fecha" },
    { Header: "Tipo", accessor: "tipo" },
    {
      Header: "Productos",
      accessor: "productos",
      Cell: ({ value }) =>
        value.map((p, idx) => (
          <div key={idx}>
            {p.kg} kg - {p.categoria} @ U$S {p.precio}/kg
          </div>
        )),
    },
    { Header: "Total Neto", accessor: "total_neto" , Cell: ({ value }) => (<b>U$S {value}</b>) },
    { Header: "Plazo (días)", accessor: "plazo" },
    { Header: "Comprador", accessor: "comprador" },
    { Header: "Observaciones", accessor: "observaciones" },
    {
      Header: "Acciones",
      accessor: "acciones",
      Cell: ({ row }) => (
        <IconButton color="primary" onClick={() => openVentaModal(row.original)}>
          <Edit />
        </IconButton>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom color="primary">
            Ventas
          </Typography>
          <Button variant="contained" color="primary" startIcon={<AddShoppingCart />} onClick={() => openVentaModal()}>
            Nueva Venta
          </Button>
          <DataTable table={{ columns, rows: ventas }} isSorted entriesPerPage showTotalEntries canSearch />
        </CardContent>
      </Card>

      {/* MODAL DE CREACIÓN / EDICIÓN */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} sx={{ "& .MuiPaper-root": { bgcolor: "background.default", color: "white" } }}>
        <DialogTitle>{selectedVenta ? "Editar Venta" : "Nueva Venta"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDInput select label="Tipo de Venta" name="tipo" value={ventaData.tipo} onChange={handleChange} fullWidth>
              {tiposVenta.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
              ))}
            </MDInput>
            <MDInput label="Fecha" name="fecha" type="date" value={ventaData.fecha} onChange={handleChange} fullWidth />
            <MDInput label="Comprador" name="comprador" value={ventaData.comprador} onChange={handleChange} fullWidth />
            <MDInput label="Total Neto" name="total_neto" type="number" value={ventaData.total_neto} onChange={handleChange} fullWidth />
            <MDInput label="Plazo (días)" name="plazo" type="number" value={ventaData.plazo} onChange={handleChange} fullWidth />
            <MDInput label="Observaciones" name="observaciones" value={ventaData.observaciones} onChange={handleChange} fullWidth />

            <Button variant="outlined" onClick={handleAddProduct}>Agregar Producto</Button>
            {ventaData.productos.map((p, index) => (
              <MDBox key={index} display="flex" gap={2} mt={2}>
                <MDInput label="KG" type="number" value={p.kg} onChange={(e) => handleProductChange(index, "kg", e.target.value)} />
                <MDInput label="Precio USD/kg" type="number" value={p.precio} onChange={(e) => handleProductChange(index, "precio", e.target.value)} />
                <MDInput label="Categoría" value={p.categoria} onChange={(e) => handleProductChange(index, "categoria", e.target.value)} />
                <IconButton onClick={() => handleDeleteProduct(index)}><Delete /></IconButton>
              </MDBox>
            ))}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="secondary">Cancelar</Button>
          <Button onClick={handleSave} color="primary">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default Ventas;
